<script setup lang="ts">
import { gsap } from 'gsap'

const localePath = useLocalePath()
const { isLoggedIn } = useAuth()
const runtimeConfig = useRuntimeConfig()

const mobileMenuOpen = ref(false)
const navRef = ref<HTMLElement | null>(null)

// Hide nav when scrolling down past a threshold, show when scrolling up.
// Show requires accumulated upward scroll so a single nudge doesn't pop the
// nav back in — the user has to actually keep scrolling up for a while.
// Skipped while the mobile menu is open so users can still close it.
let lastY = 0
let hidden = false
let ticking = false
let upScrollAccum = 0
const SHOW_AFTER_UP = 220 // px of cumulative upward scroll required to show
const HIDE_THRESHOLD = 6 // px of downward scroll required to hide
const HIDE_AFTER = 80 // ignore tiny scrolls near the very top
const JUMP = 200 // a single-frame delta this large is a programmatic jump, not user scrolling

// Right after load the browser restores the saved scroll position and Lenis
// eases to it — a stream of programmatic scrolls that must NOT be read as the
// user scrolling down, or the nav slams off-screen on reload. We stay "not
// ready" until scrolling has been quiet for a short spell, then start reacting.
let ready = false
let settleTimer: ReturnType<typeof setTimeout> | null = null

function scheduleSettle() {
  if (settleTimer) clearTimeout(settleTimer)
  settleTimer = setTimeout(() => {
    ready = true
    lastY = window.scrollY
  }, 300)
}

function onScroll() {
  if (ticking) return
  ticking = true
  requestAnimationFrame(() => {
    const y = window.scrollY
    const delta = y - lastY

    // Still settling after load: keep the baseline synced but don't react, and
    // push the settle deadline out so we only start once scrolling goes quiet.
    if (!ready) {
      lastY = y
      scheduleSettle()
      ticking = false
      return
    }

    // A single-frame jump this large isn't user scrolling — it's a hash-anchor
    // jump. Resync and bail so it isn't read as a downward scroll that hides the
    // nav off-screen.
    if (Math.abs(delta) > JUMP) {
      lastY = y
      ticking = false
      return
    }

    if (!mobileMenuOpen.value) {
      if (y <= HIDE_AFTER && hidden) {
        // Near the top — always show regardless of accumulator
        hidden = false
        upScrollAccum = 0
        gsap.to(navRef.value, { yPercent: 0, duration: 0.35, ease: 'power3.out' })
      }
      else if (y > HIDE_AFTER && delta > HIDE_THRESHOLD && !hidden) {
        hidden = true
        upScrollAccum = 0
        gsap.to(navRef.value, { yPercent: -110, duration: 0.35, ease: 'power3.out' })
      }
      else if (delta < 0 && hidden) {
        // Scrolling up while hidden — accumulate until past the threshold
        upScrollAccum += -delta
        if (upScrollAccum >= SHOW_AFTER_UP) {
          hidden = false
          upScrollAccum = 0
          gsap.to(navRef.value, { yPercent: 0, duration: 0.35, ease: 'power3.out' })
        }
      }
      else if (delta > 0) {
        // Any downward movement resets the upward accumulator
        upScrollAccum = 0
      }
    }

    lastY = y
    ticking = false
  })
}

onMounted(() => {
  lastY = window.scrollY
  window.addEventListener('scroll', onScroll, { passive: true })
  // Kick off the settle timer even if no restoration scroll fires (e.g. reload
  // at the top), so the nav starts reacting after a beat.
  scheduleSettle()
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  if (settleTimer) clearTimeout(settleTimer)
})

// If the mobile menu opens while the nav is hidden, slide it back into view.
watch(mobileMenuOpen, (open) => {
  if (open && hidden) {
    hidden = false
    gsap.to(navRef.value, { yPercent: 0, duration: 0.3, ease: 'power3.out' })
  }
})
</script>

<template>
  <nav ref="navRef" class="site-nav fixed top-0 left-0 w-full z-[100] bg-white/50 backdrop-blur-md border-b border-crystal-falls will-change-transform" role="navigation" aria-label="Main navigation">
    <div class="flex items-center justify-between gap-4 lg:gap-10 py-3 md:py-4 lg:py-4.5 container">
      <NuxtLink :to="localePath('/')" class="shrink-0">
        <img src="/h-logo.svg" alt="bookforme" width="150" height="32" class="h-8 md:h-10 w-auto">
      </NuxtLink>

      <ul class="hidden lg:flex items-center gap-1" role="list">
        <li>
          <NuxtLink :to="localePath('/')" class="inline-flex items-center px-3 py-2 rounded-lg text-base font-bold text-gray-900 transition-colors hover:bg-gray-100">{{ $t('label.home') }}</NuxtLink>
        </li>
        <li>
          <NuxtLink :to="localePath('/features')" class="inline-flex items-center px-3 py-2 rounded-lg text-base font-bold text-gray-900 transition-colors hover:bg-gray-100">{{ $t('page.home.features') }}</NuxtLink>
        </li>
        <li>
          <NuxtLink :to="localePath('/business-types')" class="inline-flex items-center px-3 py-2 rounded-lg text-base font-bold text-gray-900 transition-colors hover:bg-gray-100">{{ $t('page.home.services') }}</NuxtLink>
        </li>
        <li>
          <NuxtLink :to="localePath('/contact-us')" class="inline-flex items-center px-3 py-2 rounded-lg text-base font-bold text-gray-900 transition-colors hover:bg-gray-100">{{ $t('page.home.contact_us') }}</NuxtLink>
        </li>
      </ul>

      <div class="hidden lg:flex items-center gap-2 shrink-0">
        <UPopover
          v-if="!isLoggedIn"
          :ui="{ rounded: 'rounded-xl', ring: 'ring-1 ring-gray-200', shadow: 'shadow-lg' }"
        >
          <button class="bfm-btn bfm-btn-sm bg-cheese-yellow whitespace-nowrap flex items-center gap-1.5">
            {{ $t('menu.sign_in') }}
            <UIcon name="i-heroicons-chevron-down-20-solid" class="size-4 shrink-0" />
          </button>
          <template #panel="{ close }">
            <div class="flex flex-col gap-1 p-2 min-w-56">
              <a
                :href="(runtimeConfig.public.partnerUrl as string) || localePath('/')"
                class="flex flex-col px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                @click="close"
              >
                <span class="text-sm font-semibold text-gray-900">{{ $t('page.sign_in.for_partners') }}</span>
                <span class="text-xs text-gray-500 mt-0.5">{{ $t('page.sign_in.for_partners_desc') }}</span>
              </a>
              <NuxtLink
                :to="localePath('/sign-in')"
                class="flex flex-col px-3 py-2.5 rounded-lg hover:bg-gray-100 transition-colors"
                @click="close"
              >
                <span class="text-sm font-semibold text-gray-900">{{ $t('page.sign_in.for_all') }}</span>
                <span class="text-xs text-gray-500 mt-0.5">{{ $t('page.sign_in.for_all_desc') }}</span>
              </NuxtLink>
            </div>
          </template>
        </UPopover>
        <CommonClientDropdown v-else rounded="rounded-lg" />
      </div>

      <div class="lg:hidden flex items-center gap-2 ms-auto shrink-0">
        <CommonClientDropdown v-if="isLoggedIn" rounded="rounded-lg" size="size-9" />
        <UPopover v-model:open="mobileMenuOpen">
          <UButton
            color="white"
            :class="['nav-hamburger flex flex-col justify-center gap-1 w-9 h-9 cursor-pointer rounded-lg p-2 border border-gray-300 bg-white hover:bg-gray-50', { active: mobileMenuOpen }]"
            aria-label="Menu">
            <span class="block w-full h-0.5 bg-gray-700 rounded-sm transition-all duration-300"/>
            <span class="block w-full h-0.5 bg-gray-700 rounded-sm transition-all duration-300"/>
            <span class="block w-full h-0.5 bg-gray-700 rounded-sm transition-all duration-300"/>
          </UButton>

          <template #panel="{ close }">
            <div class="flex flex-col gap-1 p-3 w-52 max-w-[80vw]" role="navigation" aria-label="Mobile menu">
              <NuxtLink :to="localePath('/')" class="inline-flex items-center px-3 py-2 rounded-lg text-sm font-bold text-gray-900 transition-colors hover:bg-gray-100" @click="close">{{ $t('label.home') }}</NuxtLink>
              <NuxtLink :to="localePath('/features')" class="inline-flex items-center px-3 py-2 rounded-lg text-sm font-bold text-gray-900 transition-colors hover:bg-gray-100" @click="close">{{ $t('page.home.features') }}</NuxtLink>
              <NuxtLink :to="localePath('/business-types')" class="inline-flex items-center px-3 py-2 rounded-lg text-sm font-bold text-gray-900 transition-colors hover:bg-gray-100" @click="close">{{ $t('page.home.services') }}</NuxtLink>
              <NuxtLink :to="localePath('/contact-us')" class="inline-flex items-center px-3 py-2 rounded-lg text-sm font-bold text-gray-900 transition-colors hover:bg-gray-100" @click="close">{{ $t('page.home.contact_us') }}</NuxtLink>
              <template v-if="!isLoggedIn">
                <div class="mt-1.5 border-t border-gray-100 pt-2 flex flex-col gap-0.5">
                  <p class="px-3 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wider">{{ $t('menu.sign_in') }}</p>
                  <a
                    :href="(runtimeConfig.public.partnerUrl as string) || localePath('/')"
                    class="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    @click="close"
                  >
                    <div class="flex flex-col min-w-0">
                      <span class="text-sm font-semibold text-gray-900">{{ $t('page.sign_in.for_partners') }}</span>
                      <span class="text-xs text-gray-500 mt-0.5">{{ $t('page.sign_in.for_partners_desc') }}</span>
                    </div>
                  </a>
                  <NuxtLink
                    :to="localePath('/sign-in')"
                    class="flex items-start gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                    @click="close"
                  >
                    <div class="flex flex-col min-w-0">
                      <span class="text-sm font-semibold text-gray-900">{{ $t('page.sign_in.for_all') }}</span>
                      <span class="text-xs text-gray-500 mt-0.5">{{ $t('page.sign_in.for_all_desc') }}</span>
                    </div>
                  </NuxtLink>
                </div>
              </template>
            </div>
          </template>
        </UPopover>
      </div>
    </div>
  </nav>
</template>
